import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Trash2, AlertCircle, CalendarDays, ToggleLeft, ToggleRight } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export default function EventsPage() {
  const { selectedBranch, branchesForSelectedSchool } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const perms = user?.permissions || {};
  const canCreate = isOwner || !!perms.events_create;
  const canEdit = isOwner || !!perms.events_edit;
  const canDelete = isOwner || !!perms.events_delete;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const branchId = selectedBranch?.id;

  const load = useCallback(async () => {
    if (!branchId) { setEvents([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/events", { params: { branch_id: branchId } });
      setEvents(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const toggleStatus = async (ev) => {
    const next = ev.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/events/${ev.id}`, { status: next });
      toast.success(`Event ${next}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!selectedBranch) {
    return (
      <>
        <Header />
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
          <p className="text-brand-inkMuted mt-1 text-sm">Events are scoped per branch.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header onAdd={canCreate ? () => { setEditing(null); setOpen(true); } : null} />

      <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm" data-testid="events-table-wrap">
        {loading ? (
          <p className="p-6 text-brand-inkMuted">Loading...</p>
        ) : events.length === 0 ? (
          <div className="p-10 text-center" data-testid="events-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
              <CalendarDays className="w-7 h-7 text-brand-purple" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No events yet</p>
            <p className="text-brand-inkMuted mt-1 text-sm">Click Add Event to plan your first one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="events-table">
              <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                <tr>
                  <th className="text-left px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">EVENT NAME</th>
                  <th className="text-left px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">DATE</th>
                  <th className="text-right px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">FEE / AMOUNT</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">QUANTITY</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">STATUS</th>
                  <th className="text-center px-5 py-3 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => (
                  <tr key={ev.id} className={i % 2 ? "bg-white" : "bg-[#FAFBFE]"} data-testid={`event-row-${ev.id}`}>
                    <td className="px-5 py-3.5 font-heading font-semibold text-foreground whitespace-nowrap">{ev.name}</td>
                    <td className="px-5 py-3.5 text-foreground whitespace-nowrap">{ev.event_date || <span className="italic text-brand-inkMuted">—</span>}</td>
                    <td className="px-5 py-3.5 text-right tabular-nums text-foreground whitespace-nowrap">{fmt(ev.fee_amount)}</td>
                    <td className="px-5 py-3.5 text-center tabular-nums text-foreground whitespace-nowrap">{ev.quantity ?? 0}</td>
                    <td className="px-5 py-3.5 text-center whitespace-nowrap">
                      <StatusPill status={ev.status} />
                    </td>
                    <td className="px-5 py-3.5 text-center whitespace-nowrap">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" data-testid={`event-actions-${ev.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                          {canEdit && (
                            <DropdownMenuItem onClick={() => { setEditing(ev); setOpen(true); }} data-testid={`event-edit-${ev.id}`}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <DropdownMenuItem onClick={() => toggleStatus(ev)} data-testid={`event-toggle-${ev.id}`}>
                              {ev.status === "active" ? <ToggleLeft className="w-4 h-4 mr-2" /> : <ToggleRight className="w-4 h-4 mr-2" />}
                              {ev.status === "active" ? "Deactivate" : "Activate"}
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem onClick={() => setDeleting(ev)} className="text-[#C62828]" data-testid={`event-delete-${ev.id}`}>
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open && (
        <EventFormDialog
          open={open}
          onClose={() => { setOpen(false); setEditing(null); }}
          defaultBranchId={branchId}
          branches={branchesForSelectedSchool}
          currency={currency}
          editing={editing}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this event?</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-inkMuted">
              <span className="font-heading text-foreground">{deleting?.name}</span> will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/events/${deleting.id}`);
                  toast.success("Event deleted");
                  setDeleting(null);
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="rounded-2xl bg-[#C62828] hover:bg-[#B71C1C] text-white"
              data-testid="confirm-event-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Header({ onAdd }) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Events</h1>
        <p className="text-brand-inkMuted mt-0.5 text-sm">Plan parent-teacher meetings, fests and other school events.</p>
      </div>
      {onAdd && (
        <Button onClick={onAdd} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm h-11 px-5" data-testid="add-event-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Event
        </Button>
      )}
    </div>
  );
}

function EventFormDialog({ open, onClose, defaultBranchId, branches, currency, editing, onSaved }) {
  const init = editing || {};
  const [name, setName] = useState(init.name || "");
  const [eventDate, setEventDate] = useState(init.event_date || "");
  const [feeAmount, setFeeAmount] = useState(init.fee_amount != null ? String(init.fee_amount) : "0");
  const [quantity, setQuantity] = useState(init.quantity != null ? String(init.quantity) : "0");
  const [branchId, setBranchId] = useState(init.branch_id || defaultBranchId || "");
  const [status, setStatus] = useState(init.status || "active");
  const [description, setDescription] = useState(init.description || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Event name is required"); return; }
    if (!branchId) { toast.error("Branch is required"); return; }
    const payload = {
      name: name.trim(),
      event_date: eventDate || null,
      fee_amount: Number(feeAmount || 0),
      quantity: Number(quantity || 0),
      branch_id: branchId,
      status,
      description: description.trim(),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/events/${editing.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event added");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit Event" : "Add Event"}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">Fill out the event details below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="event-name" className="font-heading">Event Name <span className="text-[#C62828]">*</span></Label>
            <Input
              id="event-name" value={name} onChange={(e) => setName(e.target.value)}
              className="rounded-2xl h-11 mt-1.5"
              data-testid="event-name-input"
              autoFocus
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="event-date" className="font-heading">Event Date</Label>
              <Input
                id="event-date" type="date" value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="rounded-2xl h-11 mt-1.5"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <Label htmlFor="event-fee" className="font-heading">Fee / Amount</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted">{currency}</span>
                <Input
                  id="event-fee" type="number" min="0" step="0.01" value={feeAmount}
                  onChange={(e) => setFeeAmount(e.target.value)}
                  className="rounded-2xl h-11 pl-8 tabular-nums"
                  data-testid="event-fee-input"
                />
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="event-qty" className="font-heading">Quantity</Label>
              <Input
                id="event-qty" type="number" min="0" value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="rounded-2xl h-11 mt-1.5 tabular-nums"
                data-testid="event-qty-input"
              />
            </div>
            <div>
              <Label className="font-heading">Branch <span className="text-[#C62828]">*</span></Label>
              <select
                value={branchId} onChange={(e) => setBranchId(e.target.value)}
                className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
                data-testid="event-branch-select"
              >
                <option value="">Select branch</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="font-heading">Status</Label>
            <select
              value={status} onChange={(e) => setStatus(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm"
              data-testid="event-status-select"
            >
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div>
            <Label htmlFor="event-desc" className="font-heading">Description</Label>
            <Textarea
              id="event-desc" value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-2xl min-h-[110px] mt-1.5"
              data-testid="event-desc-input"
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11" data-testid="event-cancel-btn">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="event-submit-btn">
              {saving ? "Saving..." : (editing ? "Save" : "Add event")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status }) {
  const active = status === "active";
  return (
    <span
      className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-[12px] font-heading font-semibold text-white ${
        active ? "bg-brand-purple" : "bg-brand-inkMuted"
      }`}
      data-testid={`event-status-${status}`}
    >
      {active ? "active" : "inactive"}
    </span>
  );
}
